import React, { useCallback, useState } from "react";
import { Button, FileUpload, ModalV2, toast, type FileObjectType } from "@jsq/ds/core";

interface UploadModalProps {
  show: boolean;
  onHide: () => void;
  title: string;
  description: string;
  onUpload: (file: File) => Promise<void>;
}

export const UploadModal = ({
  show,
  onHide,
  title,
  description,
  onUpload,
}: UploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<FileObjectType | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setIsUploading(false);
    onHide();
  }, [onHide]);

  const handleFileSelected = useCallback((files: FileObjectType[]) => {
    const accepted = files.find((f) => f.status === "accepted");
    // Clear when the new pick is rejected/removed so a stale prior file can't be uploaded.
    setSelectedFile(accepted ?? null);
  }, []);

  const handleExtract = useCallback(async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    try {
      await onUpload(selectedFile.file);
      toast.success("Document uploaded. Processing will begin shortly.");
      handleClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Upload failed. Try again.");
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, onUpload, handleClose]);

  return (
    <ModalV2
      show={show}
      onHide={handleClose}
      title={title}
      description={description}
      // Lock the modal while an upload is in flight so it can't be dismissed
      // mid-request (which would orphan the upload / update state after unmount).
      closeButton={!isUploading}
      escapeToClose={!isUploading}
      closeOnClickOutside={!isUploading}
    >
      <ModalV2.Body>
        <FileUpload
          isFullWidth
          canAllowMultiple={false}
          onFileSelected={handleFileSelected}
          acceptedFileTypes={["application/pdf"]}
          isDisabled={isUploading}
          data-testid="file-upload-input"
        />
        <Button
          variant="primary"
          isFullWidth
          onClick={() => void handleExtract()}
          disabled={!selectedFile || isUploading}
          isLoading={isUploading}
          data-testid="extract-button"
        >
          Upload
        </Button>
      </ModalV2.Body>
    </ModalV2>
  );
};
